import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiFilter, FiGrid, FiList, FiX, FiStar, FiSearch, FiHeart, FiEye, FiShoppingCart, FiChevronRight } from 'react-icons/fi';
import { BsLightbulb, BsToggles, BsPlugin, BsFan, BsBatteryCharging, BsTools, BsGrid, BsCpu, BsThermometer, BsPlug, BsSpeedometer } from 'react-icons/bs';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { primaryImageUrl } from '../utils/imageUrl';
import { categoryLabel, CATEGORY_LABELS } from '../utils/categories';
import './Shop.css';

// Map category IDs to icons for the sidebar
const CATEGORY_ICONS = {
  LED_LIGHTS: <BsLightbulb size={16} />,
  SWITCHES: <BsToggles size={16} />,
  WIRES_CABLES: <BsPlugin size={16} />,
  FANS: <BsFan size={16} />,
  INVERTER: <BsBatteryCharging size={16} />,
  TOOLS: <BsTools size={16} />,
  APPLIANCES: <BsGrid size={16} />,
  ACCESSORIES: <BsCpu size={16} />,
  SERVICE: <BsTools size={16} />,
  CIRCUIT_BREAKERS: <BsToggles size={16} />,
  HEATERS: <BsThermometer size={16} />,
  PANELS_BOARDS: <BsGrid size={16} />,
  PLUGS_SOCKETS: <BsPlug size={16} />,
  EXHAUST_FANS: <BsFan size={16} />,
  WATER_HEATERS: <BsThermometer size={16} />,
  STABILIZERS: <BsBatteryCharging size={16} />,
  METERS: <BsSpeedometer size={16} />,
};

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist: contextToggleWishlist } = useWishlist();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  // Network/load error state — drives the "Slow connection — Retry" UI
  const [loadError, setLoadError] = useState(null);
  const [retryToken, setRetryToken] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    brand: searchParams.get('brand') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    minRating: searchParams.get('minRating') || '',
    sort: searchParams.get('sort') || 'newest',
    page: 1,
  });

  // Separate local state for the search box so we can debounce it
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [sidebarSearch, setSidebarSearch] = useState('');
  const searchDebounceRef = useRef(null);
  const productsTopRef = useRef(null);

  // Sync URL params to filter state when URL changes
  useEffect(() => {
    const newSearch = searchParams.get('search') || '';
    setSearchInput(newSearch);
    setFilters(prev => ({
      ...prev,
      search: newSearch,
      category: searchParams.get('category') || '',
      brand: searchParams.get('brand') || '',
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
      minRating: searchParams.get('minRating') || '',
      sort: searchParams.get('sort') || 'newest',
      page: 1,
    }));
  }, [searchParams]);

  // Always show ALL categories from the static list
  useEffect(() => {
    const allCats = Object.keys(CATEGORY_LABELS).map(id => ({
      id,
      name: CATEGORY_LABELS[id],
      count: null,
    }));
    setCategories(allCats);

    const fetchSidebar = async () => {
      try {
        const [categoriesRes, brandsRes] = await Promise.allSettled([
          api.get('/products/categories'),
          api.get('/products/brands'),
        ]);
        if (categoriesRes.status === 'fulfilled') {
          const apiCounts = {};
          (categoriesRes.value.data.data || []).forEach(c => { apiCounts[c._id] = c.count; });
          setCategories(Object.keys(CATEGORY_LABELS).map(id => ({
            id,
            name: CATEGORY_LABELS[id],
            count: apiCounts[id] ?? 0,
          })));
        }
        if (brandsRes.status === 'fulfilled') {
          setBrands((brandsRes.value.data.data || []).map(brand => ({
            id: brand._id,
            name: brand._id,
            count: brand.count,
          })));
        }
      } catch (_) {}
    };
    fetchSidebar();
  }, []);

  // Fetch products whenever filters change (or retry is triggered)
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const res = await api.get('/products', { params: { ...filters, limit: 16 } });
        setProducts(res.data.data || []);
        if (res.data.pagination) setPagination(res.data.pagination);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching products:', error);
        // The api interceptor already retried twice. If we still failed,
        // surface the error in-page so the user can decide to retry rather
        // than just see an empty grid + toast they may have missed.
        const isNetworkErr = !error.response;
        setLoadError({
          message: isNetworkErr
            ? 'We couldn\'t reach our servers. Check your connection and try again.'
            : (error.response?.data?.message || 'Something went wrong. Please try again.'),
          isNetwork: isNetworkErr,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [filters, retryToken]);

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    if (productsTopRef.current) {
      const y = productsTopRef.current.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const applyFilter = useCallback((key, value) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== 'newest' && v !== 1) params.set(k, v);
    });
    navigate(`/shop?${params.toString()}`);
    setMobileFilterOpen(false);
  }, [filters, navigate]);

  const handleFilterChange = (key, value) => {
    if (key === 'search') {
      setSearchInput(value);
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => applyFilter('search', value), 450);
    } else {
      applyFilter(key, value);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      brand: '',
      minPrice: '',
      maxPrice: '',
      minRating: '',
      sort: 'newest',
      page: 1,
    });
    setSearchInput('');
    setSidebarSearch('');
    navigate('/shop');
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.stock === 0) {
      toast.error('Out of stock');
      return;
    }
    addToCart(product._id, 1);
    toast.success(`${product.name} added to cart`);
  };

  const toggleWishlist = (e, productId, productName) => {
    e.preventDefault();
    e.stopPropagation();
    contextToggleWishlist(productId, productName);
  };

  const activeFiltersCount = [
    filters.search,
    filters.category,
    filters.brand,
    filters.minPrice,
    filters.maxPrice,
    filters.minRating,
  ].filter(Boolean).length;

  // Get the current category name for the hero
  const currentCategoryName = filters.category
    ? CATEGORY_LABELS[filters.category] || filters.category.replace(/_/g, ' ')
    : 'All Products';

  return (
    <>
      <Helmet>
        <title>{currentCategoryName} - Ceedees Electricals</title>
        <meta name="description" content="Browse our wide range of electrical products" />
      </Helmet>

      <div className="shop-page-v2">
        {/* Hero Banner */}
        <div className="shop-hero">
          <div className="shop-hero-bulbs">
            <div className="bulb-wire" /><div className="bulb-wire" /><div className="bulb-wire" />
            <div className="bulb-wire" /><div className="bulb-wire" />
          </div>
          <div className="container shop-hero-content">
            <nav className="shop-breadcrumb">
              <Link to="/">Home</Link>
              <FiChevronRight size={12} />
              <span>{currentCategoryName}</span>
            </nav>
            <h1>{currentCategoryName}</h1>
            <p>Discover premium electrical products for every need</p>
          </div>
        </div>

        <div className="container shop-layout">
          {/* Sidebar */}
          <aside className={`shop-sidebar-v2 ${mobileFilterOpen ? 'open' : ''}`}>
            <div className="sidebar-header-v2">
              <h3>Filters</h3>
              <button className="close-btn-v2" onClick={() => setMobileFilterOpen(false)}>
                <FiX size={18} />
              </button>
            </div>

            {/* Sidebar Search */}
            <div className="sidebar-search">
              <FiSearch size={15} className="sidebar-search-icon" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchInput}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>

            {/* Categories */}
            <div className="filter-section">
              <h4 className="filter-section-title">Categories</h4>
              <div className="category-list">
                <button
                  className={`category-item ${!filters.category ? 'active' : ''}`}
                  onClick={() => handleFilterChange('category', '')}
                >
                  <BsGrid size={16} />
                  <span>All Categories</span>
                  {!filters.category && <span className="cat-check">&#10003;</span>}
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`category-item ${filters.category === cat.id ? 'active' : ''}`}
                    onClick={() => handleFilterChange('category', cat.id)}
                  >
                    {CATEGORY_ICONS[cat.id] || <BsGrid size={16} />}
                    <span>{cat.name}</span>
                    {cat.count !== null && <span className="cat-count">{cat.count}</span>}
                    {filters.category === cat.id && <span className="cat-check">&#10003;</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Brands */}
            {brands.length > 0 && (
              <div className="filter-section">
                <h4 className="filter-section-title">Brand</h4>
                <div className="brand-list">
                  <label className={`brand-item ${!filters.brand ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="brand"
                      value=""
                      checked={!filters.brand}
                      onChange={() => handleFilterChange('brand', '')}
                    />
                    <span className="brand-radio" />
                    <span>All Brands</span>
                  </label>
                  {brands.map((brand) => (
                    <label key={brand.id} className={`brand-item ${filters.brand === brand.id ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="brand"
                        value={brand.id}
                        checked={filters.brand === String(brand.id)}
                        onChange={() => handleFilterChange('brand', brand.id)}
                      />
                      <span className="brand-radio" />
                      <span>{brand.name}</span>
                      <span className="brand-count">{brand.count}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Price Range */}
            <div className="filter-section">
              <h4 className="filter-section-title">Price Range</h4>
              <div className="price-range-inputs">
                <div className="price-input-wrap">
                  <span className="price-symbol">₹</span>
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  />
                </div>
                <span className="price-dash">—</span>
                <div className="price-input-wrap">
                  <span className="price-symbol">₹</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Minimum Rating */}
            <div className="filter-section">
              <h4 className="filter-section-title">Minimum Rating</h4>
              <select
                className="rating-filter-select"
                value={filters.minRating || ''}
                onChange={(e) => handleFilterChange('minRating', e.target.value)}
              >
                <option value="">All Ratings</option>
                <option value="4">4&#9733; &amp; above</option>
                <option value="3">3&#9733; &amp; above</option>
                <option value="2">2&#9733; &amp; above</option>
                <option value="1">1&#9733; &amp; above</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="filter-actions">
              <button className="filter-apply-btn" onClick={() => setMobileFilterOpen(false)}>
                Apply Filters
              </button>
              {activeFiltersCount > 0 && (
                <button className="filter-reset-btn" onClick={clearFilters}>
                  Reset All
                </button>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <section className="shop-main-v2">
            <div ref={productsTopRef} style={{ position: 'absolute', marginTop: '-8px' }} />

            {/* Toolbar */}
            <div className="shop-toolbar-v2">
              <div className="toolbar-left-v2">
                <button className="filter-toggle-v2" onClick={() => setMobileFilterOpen(true)}>
                  <FiFilter size={15} />
                  Filters
                  {activeFiltersCount > 0 && <span className="filter-badge-v2">{activeFiltersCount}</span>}
                </button>
                <span className="results-count-v2">
                  {loading ? 'Loading...' : <>Showing <strong>{pagination.total || products.length}</strong> products</>}
                </span>
              </div>

              <div className="toolbar-right-v2">
                <div className="sort-wrap">
                  <label>Sort by:</label>
                  <select
                    value={filters.sort}
                    onChange={(e) => handleFilterChange('sort', e.target.value)}
                  >
                    <option value="newest">Latest</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="rating">Top Rated</option>
                    <option value="popular">Most Popular</option>
                  </select>
                </div>
                <div className="view-toggle">
                  <button
                    className={`view-btn-v2 ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid view"
                  >
                    <FiGrid size={15} />
                  </button>
                  <button
                    className={`view-btn-v2 ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List view"
                  >
                    <FiList size={15} />
                  </button>
                </div>
              </div>
            </div>

            {/* Active filter chips */}
            {activeFiltersCount > 0 && (
              <div className="active-filters-v2">
                {filters.search && (
                  <button className="chip-v2" onClick={() => handleFilterChange('search', '')}>
                    Search: &ldquo;{filters.search}&rdquo; <FiX size={12} />
                  </button>
                )}
                {filters.category && (
                  <button className="chip-v2" onClick={() => handleFilterChange('category', '')}>
                    {categoryLabel(filters.category)} <FiX size={12} />
                  </button>
                )}
                {filters.brand && (
                  <button className="chip-v2" onClick={() => handleFilterChange('brand', '')}>
                    {filters.brand} <FiX size={12} />
                  </button>
                )}
                {(filters.minPrice || filters.maxPrice) && (
                  <button className="chip-v2" onClick={() => { handleFilterChange('minPrice', ''); handleFilterChange('maxPrice', ''); }}>
                    ₹{filters.minPrice || '0'} – ₹{filters.maxPrice || '∞'} <FiX size={12} />
                  </button>
                )}
                {filters.minRating && (
                  <button className="chip-v2" onClick={() => handleFilterChange('minRating', '')}>
                    {filters.minRating}★ &amp; above <FiX size={12} />
                  </button>
                )}
                <button className="chip-v2 chip-clear" onClick={clearFilters}>
                  Clear all <FiX size={12} />
                </button>
              </div>
            )}

            {/* Products Grid */}
            {loading ? (
              <div className={viewMode === 'grid' ? 'products-grid-v2' : 'products-list-v2'}>
                {[1,2,3,4,5,6,7,8].map((i) => (
                  <div key={i} className="skeleton-card-v2">
                    <div className="sk-image-v2" />
                    <div className="sk-body-v2">
                      <div className="sk-pill-v2" />
                      <div className="sk-title-v2" />
                      <div className="sk-title-short-v2" />
                      <div className="sk-price-v2" />
                      <div className="sk-btn-v2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : loadError ? (
              <div className="shop-error-state">
                <div className="shop-error-icon">{loadError.isNetwork ? '📡' : '⚠️'}</div>
                <h3 className="shop-error-title">
                  {loadError.isNetwork ? 'Connection seems slow' : 'Something went wrong'}
                </h3>
                <p className="shop-error-message">{loadError.message}</p>
                <button
                  className="shop-error-retry"
                  onClick={() => setRetryToken(t => t + 1)}
                  disabled={loading}
                >
                  Try again
                </button>
                <p className="shop-error-hint">Still stuck? Try opening WiFi or moving to better signal.</p>
              </div>
            ) : products.length > 0 ? (
              <div className={viewMode === 'grid' ? 'products-grid-v2' : 'products-list-v2'}>
                {products.filter(p => p.slug).map((product) => {
                  const discount = product.mrp > product.sellingPrice
                    ? Math.round((1 - product.sellingPrice / product.mrp) * 100)
                    : 0;
                  const isWishlisted = isInWishlist(product._id);
                  return viewMode === 'grid' ? (
                    <div key={product._id} className="product-card-v2">
                      <div className="card-image-wrap">
                        <Link to={`/product/${product.slug}`}>
                          <img
                            src={primaryImageUrl(product)}
                            alt={product.name}
                            loading="lazy"
                            decoding="async"
                          />
                        </Link>
                        {discount > 0 && (
                          <span className="card-discount">{discount}% OFF</span>
                        )}
                        <button
                          className={`card-wishlist ${isWishlisted ? 'wishlisted' : ''}`}
                          onClick={(e) => toggleWishlist(e, product._id, product.name)}
                          title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                        >
                          <FiHeart size={16} />
                        </button>
                      </div>
                      <div className="card-body-v2">
                        <span className="card-category">{categoryLabel(product.category)}</span>
                        <Link to={`/product/${product.slug}`} className="card-title-link">
                          <h4 className="card-title-v2">{product.name}</h4>
                        </Link>
                        <div className="card-rating">
                          {[...Array(5)].map((_, i) => (
                            <FiStar key={i} size={13} className={i < Math.round(product.averageRating || 0) ? 'star-filled' : 'star-empty'} />
                          ))}
                          <span className="rating-count">({product.reviewCount || 0})</span>
                        </div>
                        <div className="card-pricing">
                          <span className="card-price">₹{product.sellingPrice.toLocaleString('en-IN')}</span>
                          {product.mrp > product.sellingPrice && (
                            <>
                              <span className="card-mrp">₹{product.mrp.toLocaleString('en-IN')}</span>
                              <span className="card-save">Save ₹{(product.mrp - product.sellingPrice).toLocaleString('en-IN')}</span>
                            </>
                          )}
                        </div>
                        <div className="card-actions">
                          <button
                            className="add-to-cart-btn"
                            onClick={(e) => handleAddToCart(e, product)}
                            disabled={product.stock === 0}
                          >
                            <FiShoppingCart size={14} />
                            <span className="cart-btn-text">{product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}</span>
                          </button>
                          <Link to={`/product/${product.slug}`} className="view-btn-card" title="Quick view">
                            <FiEye size={16} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link key={product._id} to={`/product/${product.slug}`} className="product-row-v2">
                      <div className="row-image">
                        <img src={primaryImageUrl(product)} alt={product.name} loading="lazy" />
                        {discount > 0 && <span className="card-discount">{discount}% OFF</span>}
                      </div>
                      <div className="row-details">
                        <span className="card-category">{categoryLabel(product.category)}</span>
                        <h4>{product.name}</h4>
                        <p className="row-desc">{product.description}</p>
                        <div className="card-rating">
                          {[...Array(5)].map((_, i) => (
                            <FiStar key={i} size={13} className={i < Math.round(product.averageRating || 0) ? 'star-filled' : 'star-empty'} />
                          ))}
                          <span className="rating-count">({product.reviewCount || 0})</span>
                        </div>
                        <div className="card-pricing">
                          <span className="card-price">₹{product.sellingPrice.toLocaleString('en-IN')}</span>
                          {product.mrp > product.sellingPrice && (
                            <>
                              <span className="card-mrp">₹{product.mrp.toLocaleString('en-IN')}</span>
                              <span className="card-save">Save ₹{(product.mrp - product.sellingPrice).toLocaleString('en-IN')}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="no-results-v2">
                <div className="no-results-icon-v2">🔍</div>
                <h3>No products found</h3>
                <p>Try adjusting your search or filters</p>
                <button onClick={clearFilters} className="filter-apply-btn">
                  Clear Filters
                </button>
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="shop-pagination-v2">
                <button
                  className="page-btn"
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page <= 1}
                >
                  ← Prev
                </button>
                <div className="page-numbers">
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === pagination.pages || Math.abs(p - filters.page) <= 2)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === '…' ? (
                        <span key={`ellipsis-${idx}`} className="page-ellipsis">…</span>
                      ) : (
                        <button
                          key={p}
                          className={`page-btn ${filters.page === p ? 'active' : ''}`}
                          onClick={() => handlePageChange(p)}
                        >
                          {p}
                        </button>
                      )
                    )}
                </div>
                <button
                  className="page-btn"
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page >= pagination.pages}
                >
                  Next →
                </button>
              </div>
            )}
          </section>
        </div>

        {mobileFilterOpen && (
          <div className="shop-backdrop" onClick={() => setMobileFilterOpen(false)} />
        )}
      </div>
    </>
  );
}

export default Shop;
